import { useRef } from "react";
import { CAD, ALCOHOL_DISCLAIMER, FIRE_NOTE, BRAND, IMAGES } from "@/lib/constants";
import { generateAlcoholList } from "@/lib/quote";
import { Printer, Copy, X, CreditCard } from "lucide-react";
import { toast } from "sonner";

const DRINK_IMGS = [IMAGES.cocktail, IMAGES.mocktail, IMAGES.juices, IMAGES.garnish];

const shortId = (id) => (id || "").replace(/-/g, "").slice(0, 8).toUpperCase();
const fmtDate = (d) => (d ? d : "—");
const todayStr = () => new Date().toISOString().slice(0, 10);

function DocShell({ docTitle, meta, children }) {
  return (
    <div className="print-area bg-white text-ink mx-auto p-8 sm:p-12" style={{ maxWidth: 820, fontFamily: "Manrope, sans-serif" }}>
      <header className="flex items-start justify-between border-b border-ink/15 pb-6 mb-8">
        <div className="flex items-center gap-4">
          <img src="/logo-mark.png" alt="Drunk Fruit" style={{ height: 64, width: "auto" }} />
          <div>
            <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 26, lineHeight: 1 }}>Drunk Fruit Mobile Bar</div>
            <div className="text-xs text-muted-foreground mt-1">{BRAND.email} · {BRAND.instagram}</div>
            <div className="text-xs text-muted-foreground">{BRAND.location}</div>
          </div>
        </div>
        <div className="text-right">
          <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 30 }}>{docTitle}</div>
          {meta.map((m) => (
            <div key={m.label} className="text-xs text-muted-foreground">{m.label}: <span className="text-ink">{m.value}</span></div>
          ))}
        </div>
      </header>
      {children}
      <footer className="mt-10 pt-6 border-t border-ink/15 text-[10px] leading-relaxed text-muted-foreground">
        <p className="mb-1">{ALCOHOL_DISCLAIMER}</p>
        <p>{BRAND.name} · {BRAND.email} · {BRAND.instagram}</p>
      </footer>
    </div>
  );
}

const SectionTitle = ({ children }) => (
  <h3 style={{ fontFamily: "Cormorant Garamond, serif" }} className="text-xl mb-3 mt-6">{children}</h3>
);

const InfoGrid = ({ rows }) => (
  <div className="grid grid-cols-2 gap-y-2 gap-x-8 text-sm">
    {rows.map((r) => (
      <div key={r.label} className="flex justify-between border-b border-ink/5 py-1">
        <span className="text-muted-foreground">{r.label}</span><span className="font-medium text-right">{r.value || "—"}</span>
      </div>
    ))}
  </div>
);

function clientRows(e) {
  return [
    { label: "Client", value: e.full_name },
    { label: "Email", value: e.email },
    { label: "Phone", value: e.phone },
    { label: "Event Date", value: fmtDate(e.event_date) },
    { label: "Event Type", value: e.event_type },
    { label: "Location", value: [e.event_city, e.event_address].filter(Boolean).join(", ") },
    { label: "Guests", value: `${e.guest_count || 0} (${e.adult_count || 0} adults, ${e.kids_count || 0} kids)` },
    { label: "Service Hours", value: e.service_hours ? `${e.service_hours} hrs` : "—" },
    { label: "Setup Arrival", value: e.setup_arrival_time },
    { label: "Service Time", value: [e.service_start_time, e.service_end_time].filter(Boolean).join(" – ") },
  ];
}

const SERVICE_OVERVIEW = [
  "Professional bartender service, setup, cleanup and standard bar tools.",
  "Mobile bar setup at the event location.",
  "12 oz plastic cups, ice, napkins, straws and basic setup consumables.",
  "Syrups, mixers, fresh fruit, lime, garnishes and basic rim ingredients for the confirmed menu.",
  "Owner-approved event-specific add-ons shown in the pricing table.",
];
const QUOTE_NOTES = [
  "Pricing is based on the current guest count, service hours, staffing, package, menu complexity and location.",
  "Pricing may change if guests, hours, location, menu, specialty ingredients or staffing requirements change.",
  "Availability is subject to confirmation and deposit.",
  "The remaining balance is due before service begins.",
  "Current cancellation notice reference: 15 days. Refund terms remain subject to the approved contract.",
];
const BOOKING_STEPS = [
  ["01", "Choose the menu"],
  ["02", "Review and sign the contract"],
  ["03", "Pay the 50% deposit"],
  ["04", "Receive the alcohol shopping list"],
  ["05", "Pay the remaining balance before service"],
];
const pad2 = (n) => String(n).padStart(2, "0");
function quoteRef(e) {
  const d = e.event_date ? new Date(e.event_date + "T00:00:00") : new Date();
  const mmddyy = `${pad2(d.getMonth() + 1)}${pad2(d.getDate())}${String(d.getFullYear()).slice(2)}`;
  return `DF-${mmddyy}-${(shortId(e.id) || "LOC").slice(0, 3)}`;
}

export function QuoteDoc({ e, pricing, guideUrl }) {
  const q = e.quote || {};
  const ref = quoteRef(e);
  const serif = { fontFamily: "Cormorant Garamond, serif" };
  const sheet = { maxWidth: 820, fontFamily: "Manrope, sans-serif" };

  const bReg = q.bartender_regular != null ? q.bartender_regular : q.regular_price;
  const bCust = q.bartender_customer != null ? q.bartender_customer : q.promo_price;
  const basic = q.basic_supplies != null ? q.basic_supplies : q.supplies;
  const menuIng = q.menu_ingredients != null ? q.menu_ingredients : 0;
  const rows = [
    { s: "Professional Bartender Service", d: `${q.bartenders || 1} bartender(s) × ${q.hours || 0} hrs @ ${CAD(q.rate)}/hr`, reg: bReg, cust: bCust },
    { s: "Mobile Bar Rental", d: "Mobile bar setup at your location", reg: q.mobile_bar_rental, cust: q.mobile_bar_rental },
    { s: "Basic Supplies Package", d: "Cups, napkins, straws, ice and setup consumables", reg: basic, cust: basic },
    { s: "Syrups, Garnishes, Fruit, Lime & Mixers", d: "Menu-specific non-alcoholic ingredients", reg: menuIng, cust: menuIng },
    { s: "Transportation", d: e.event_city || "Service area", reg: q.transportation, cust: q.transportation },
  ];
  const addonItems = (q.addon_items && q.addon_items.length)
    ? q.addon_items
    : (Number(q.addons_total) > 0 ? [{ label: "Add-ons", details: "Event-specific add-ons", regular: q.addons_total, customer: q.addons_total }] : []);
  addonItems.forEach((a) => rows.push({ s: a.label, d: a.details || "Event-specific add-on", reg: a.regular != null ? a.regular : a.customer, cust: a.customer }));
  const priced = rows.filter((r) => (Number(r.reg) || 0) > 0 || (Number(r.cust) || 0) > 0);

  const details = [
    ["Client", e.full_name], ["Event", e.event_type || "—"],
    ["Date", fmtDate(e.event_date)], ["Location", [e.event_city, e.event_address].filter(Boolean).join(", ") || "—"],
    ["Service Time", [e.service_start_time, e.service_end_time].filter(Boolean).join(" – ") || "To be confirmed"],
    ["Guests", `${e.guest_count || 0} total`],
    ["Total Hours", q.hours ? `${q.hours}` : (e.service_hours || "—")],
    ["Adults / Kids", `${e.adult_count || 0} / ${e.kids_count || 0}`],
    ["Bartenders", `${q.bartenders || 1}`], ["Package", e.package_type || e.service_type || "Full beverage service"],
  ];

  return (
    <div className="print-area bg-white text-ink">
      {/* PAGE 1 */}
      <div className="mx-auto p-8 sm:p-12" style={sheet}>
        <div className="flex items-start justify-between border-b border-ink/15 pb-5 mb-6">
          <div className="flex items-center gap-3">
            <img src="/logo-mark.png" alt="Drunk Fruit" style={{ height: 56 }} />
            <div>
              <div style={{ ...serif, fontSize: 22, lineHeight: 1 }}>Drunk Fruit Mobile Bar</div>
              <div className="text-[11px] text-muted-foreground mt-1">{BRAND.email} · {BRAND.instagram}</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Quote Reference</div>
            <div className="text-sm font-semibold" data-testid="quote-reference">{ref}</div>
            <div className="text-[10px] text-muted-foreground mt-1">CAD · Canadian Dollars</div>
          </div>
        </div>

        <h1 style={{ ...serif, fontSize: 34, letterSpacing: 1 }} className="uppercase mb-3">Mobile Bar Service Quote</h1>
        <p className="text-sm text-muted-foreground mb-7 max-w-2xl">Thank you for considering Drunk Fruit Mobile Bar. This quotation is prepared from the event details currently available and remains subject to confirmation and deposit.</p>

        <h3 style={serif} className="text-lg uppercase tracking-wider mb-3">Event Details</h3>
        <div className="grid grid-cols-2 gap-x-10 gap-y-1.5 text-sm mb-8">
          {details.map(([l, v]) => (
            <div key={l} className="flex justify-between border-b border-ink/5 py-1"><span className="text-muted-foreground">{l}</span><span className="font-medium text-right">{v || "—"}</span></div>
          ))}
        </div>

        <h3 style={serif} className="text-lg uppercase tracking-wider mb-3">Pricing</h3>
        <div className="rounded-lg overflow-hidden border border-ink/10" data-testid="quote-pricing-table">
          <div className="grid grid-cols-12 bg-ink text-white text-[10px] uppercase tracking-widest px-4 py-2.5">
            <span className="col-span-4">Service</span><span className="col-span-4">Details</span>
            <span className="col-span-2 text-right">Regular Value</span><span className="col-span-2 text-right">Customer Price</span>
          </div>
          {priced.map((r, i) => (
            <div key={r.s + i} className="grid grid-cols-12 px-4 py-2.5 text-sm border-t border-ink/5 items-start">
              <span className="col-span-4 font-medium">{r.s}</span>
              <span className="col-span-4 text-muted-foreground text-xs">{r.d}</span>
              <span className="col-span-2 text-right text-muted-foreground">{CAD(r.reg)}</span>
              <span className="col-span-2 text-right font-medium">{CAD(r.cust)}</span>
            </div>
          ))}
        </div>

        <div className="grid sm:grid-cols-2 gap-6 mt-6">
          <div className="rounded-lg bg-sand p-5">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">50% Deposit</div>
            <div style={serif} className="text-4xl" data-testid="quote-deposit">{CAD(q.deposit)}</div>
            <div className="text-sm text-muted-foreground mt-2">Remaining balance: <span className="text-ink font-medium">{CAD(q.balance)}</span></div>
            <div className="text-xs text-muted-foreground mt-1">A deposit is required to reserve the event date.</div>
          </div>
          <div className="text-sm">
            <div className="flex justify-between py-1"><span className="text-muted-foreground">Regular Price</span><span>{CAD(q.regular_price)}</span></div>
            <div className="flex justify-between py-1 text-olive"><span>Promotional Savings</span><span>– {CAD(q.savings)}</span></div>
            <div className="flex justify-between py-1"><span className="text-muted-foreground">Subtotal Before HST</span><span>{CAD(q.subtotal)}</span></div>
            <div className="flex justify-between py-1"><span className="text-muted-foreground">HST ({q.hst_rate ?? 13}%)</span><span>{CAD(q.hst)}</span></div>
            <div className="flex justify-between py-2 mt-1 border-t border-ink/20 font-semibold text-lg"><span>Final Total</span><span data-testid="quote-final-total">{CAD(q.total)}</span></div>
          </div>
        </div>

        <div className="mt-10 pt-5 border-t border-ink/10 flex justify-between text-[10px] text-muted-foreground">
          <span>Drunk Fruit Mobile Bar | {ref}</span><span>Page 1 of 2</span>
        </div>
      </div>

      {/* PAGE 2 */}
      <div className="mx-auto p-8 sm:p-12 border-t-8 border-cream" style={{ ...sheet, pageBreakBefore: "always" }}>
        <div className="flex items-center justify-between border-b border-ink/15 pb-5 mb-6">
          <img src="/logo-mark.png" alt="Drunk Fruit" style={{ height: 48 }} />
          <h2 style={{ ...serif, fontSize: 26 }} className="uppercase tracking-wider">Service Overview</h2>
        </div>

        <h3 style={serif} className="text-lg uppercase tracking-wider mb-3">Selected Service</h3>
        <div className="space-y-2 mb-8">
          {SERVICE_OVERVIEW.map((t, i) => (
            <div key={i} className="flex gap-3 text-sm"><span style={serif} className="text-olive w-6 shrink-0">{pad2(i + 1)}</span><span className="text-muted-foreground">{t}</span></div>
          ))}
        </div>

        <h3 style={serif} className="text-lg uppercase tracking-wider mb-3">Menu &amp; Alcohol</h3>
        <div className="text-sm text-muted-foreground space-y-2 mb-4">
          <p>Alcohol is not included in the service price. The client purchases and provides all alcoholic beverages required for the event. Drunk Fruit will prepare an alcohol shopping list after the cocktail menu is confirmed.</p>
          <p>Pricing is based on a maximum of three signature cocktails or mocktails.</p>
          <p>The client selects the final menu after accepting the quotation and before the contract is generated.</p>
        </div>
        <div className="rounded-lg bg-sand p-4 text-xs text-muted-foreground space-y-2 mb-8">
          <p>Please review the attached Cocktail &amp; Mocktail Selection Guide and send us your top 3 drink choices. You may mix cocktails and mocktails. Simple beer, wine, Champagne, Prosecco, soft drinks and basic mixed drinks do not normally count as one of the three signature menu spots.</p>
          <p>If you do not see your preferred drink, send us your idea and we will confirm availability, ingredients, presentation, service speed and whether it fits your approved quote.</p>
          {guideUrl && <p><a href={guideUrl} className="text-olive underline" target="_blank" rel="noreferrer">Open the Cocktail &amp; Mocktail Selection Guide (PDF)</a></p>}
        </div>

        <h3 style={serif} className="text-lg uppercase tracking-wider mb-3">Quote Notes</h3>
        <ul className="space-y-1.5 mb-8">
          {QUOTE_NOTES.map((t, i) => (
            <li key={i} className="flex gap-2 text-sm text-muted-foreground"><span className="text-olive">•</span><span>{t}</span></li>
          ))}
        </ul>

        <h3 style={serif} className="text-lg uppercase tracking-wider mb-3">Booking Process</h3>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-8">
          {BOOKING_STEPS.map(([n, t]) => (
            <div key={n} className="rounded-lg bg-clay p-3 text-center">
              <div style={serif} className="text-2xl text-olive">{n}</div>
              <div className="text-xs text-muted-foreground mt-1">{t}</div>
            </div>
          ))}
        </div>

        <p style={serif} className="text-right text-lg">Team Drunk Fruit</p>

        <div className="mt-6 pt-5 border-t border-ink/10 flex justify-between text-[10px] text-muted-foreground">
          <span>Drunk Fruit Mobile Bar | {ref}</span><span>Page 2 of 2</span>
        </div>
      </div>
    </div>
  );
}

export function ContractDoc({ e, pricing }) {
  const q = e.quote || {};
  const notes = [
    ["Services", `${e.package_type || "Full Service"} — mobile bar service, cocktail/mocktail preparation, mixers, garnishes, supplies, setup & presentation.`],
    ["Selected Menu", (e.selected_cocktails || []).join(", ") || e.preferred_cocktails || "To be confirmed"],
    ["Payment Terms", `${q.deposit_pct ?? 50}% deposit (${CAD(q.deposit)}) reserves the date. Balance (${CAD(q.balance)}) due before or at the start of service.`],
    ["Cancellation", "Deposits are non-refundable. Cancellations within 14 days of the event forfeit the deposit."],
    ["Alcohol Responsibility", ALCOHOL_DISCLAIMER],
    ["Liability & Safety", "Drunk Fruit staff serve responsibly and may refuse service to intoxicated or underage guests. The client ensures a safe working environment."],
    ["Outdoor / Weather", "For outdoor events, the client provides shade/cover. Drunk Fruit is not liable for weather disruptions."],
    ["Venue Access", "The client ensures timely venue access for setup ~1 hour before service."],
    ["Parking / Loading", "The client provides reasonable parking and loading access for the mobile bar."],
    ["Glassware Policy", pricing?.glassware_policy || "Premium clear 12 oz plastic cups are standard; glassware is available for corporate/large events with additional fees."],
    ["Fire / Flair Bartending", FIRE_NOTE],
  ];
  return (
    <DocShell docTitle="Service Agreement" meta={[
      { label: "Contract #", value: "C-" + shortId(e.id) },
      { label: "Date", value: todayStr() },
    ]}>
      <SectionTitle>Parties & Event</SectionTitle>
      <InfoGrid rows={clientRows(e)} />
      {notes.map(([t, body]) => (
        <div key={t} className="mt-4">
          <div className="font-semibold text-sm">{t}</div>
          <p className="text-xs text-muted-foreground leading-relaxed mt-1">{body}</p>
        </div>
      ))}
      <p className="text-xs text-muted-foreground leading-relaxed mt-4">{pricing?.default_contract_terms || ""}</p>
      {e.contract?.signature ? (
        <div className="mt-10 pt-6 border-t border-ink/15">
          <div className="text-xs uppercase tracking-widest text-olive mb-2">Signed electronically · Contract Status: Signed</div>
          <img src={e.contract.signature.signature_data} alt="Client signature" style={{ height: 70, maxWidth: 280, objectFit: "contain" }} />
          <div className="text-base font-medium mt-1">{e.contract.signature.legal_name}</div>
          <div className="text-xs text-muted-foreground">{new Date(e.contract.signature.signed_at).toLocaleString()} · Contract {e.contract.signature.version || e.contract.version || "v1"}{e.contract.signature.ip ? ` · IP ${e.contract.signature.ip}` : ""}</div>
          <div className="text-xs text-muted-foreground">{e.contract.signature.client_email}</div>
        </div>
      ) : (
        <div className="mt-10 grid grid-cols-2 gap-8 text-sm">
          <div className="pt-8 border-t border-ink/30">Client Signature / Date</div>
          <div className="pt-8 border-t border-ink/30">Drunk Fruit Mobile Bar / Date</div>
        </div>
      )}
    </DocShell>
  );
}

function InvoiceDoc({ e, pricing }) {
  const q = e.quote || {};
  const inv = e.invoice || {};
  const lines = inv.line_items && inv.line_items.length ? inv.line_items : [
    { desc: "Bartender service", amount: q.promo_price },
    { desc: "Mobile bar rental", amount: q.mobile_bar_rental },
    { desc: "Transportation", amount: q.transportation },
    { desc: "Supplies", amount: q.supplies },
    { desc: "Add-ons", amount: q.addons_total },
  ].filter((l) => Number(l.amount) > 0);
  return (
    <DocShell docTitle="Invoice" meta={[
      { label: "Invoice #", value: "INV-" + shortId(e.id) },
      { label: "Date", value: todayStr() },
      { label: "Status", value: e.payment_status },
    ]}>
      <SectionTitle>Bill To</SectionTitle>
      <InfoGrid rows={clientRows(e).slice(0, 6)} />

      <SectionTitle>Items</SectionTitle>
      <div className="rounded-xl border border-ink/10 overflow-hidden">
        <div className="flex justify-between px-4 py-2 bg-clay text-xs uppercase tracking-widest text-muted-foreground"><span>Description</span><span>Amount</span></div>
        {lines.map((l, i) => (
          <div key={i} className="flex justify-between px-4 py-2 text-sm border-t border-ink/5"><span>{l.desc}</span><span>{CAD(l.amount)}</span></div>
        ))}
      </div>
      <div className="mt-4 ml-auto" style={{ maxWidth: 320 }}>
        <div className="flex justify-between py-1 text-sm"><span>Subtotal</span><span>{CAD(q.subtotal)}</span></div>
        <div className="flex justify-between py-1 text-sm"><span>HST ({q.hst_rate ?? 13}%)</span><span>{CAD(q.hst)}</span></div>
        <div className="flex justify-between py-2 border-t border-ink/15 font-semibold text-lg"><span>Total</span><span>{CAD(q.total)}</span></div>
        <div className="flex justify-between py-1 text-sm"><span>Deposit required</span><span>{CAD(q.deposit)}</span></div>
        <div className="flex justify-between py-1 text-sm text-olive"><span>Deposit paid</span><span>{CAD(inv.deposit_paid || 0)}</span></div>
        <div className="flex justify-between py-1 text-sm font-medium"><span>Balance due</span><span>{CAD(q.total - (inv.deposit_paid || 0))}</span></div>
      </div>

      <SectionTitle>Payment</SectionTitle>
      <p className="text-sm">Pay securely via Square: <span className="text-olive break-all">{inv.square_link || "Payment link to be provided"}</span></p>
      <p className="text-xs text-muted-foreground mt-2 leading-relaxed">{pricing?.default_invoice_notes || ""}</p>
    </DocShell>
  );
}

function MenuDoc({ e, kids }) {
  const menu = (kids ? e.kids_menu : e.menu) || {};
  const drinks = kids
    ? [menu.drink1, menu.drink2, menu.drink3].map((d, i) => d || {})
    : [menu.c1, menu.c2, menu.c3].map((d, i) => d || {});
  const fallback = (e.selected_cocktails || []);
  return (
    <DocShell docTitle={kids ? "Kids Menu" : "Event Menu"} meta={[{ label: "Event", value: e.event_date }]}>
      <div className="text-center my-4">
        <div style={{ fontFamily: "Cormorant Garamond, serif" }} className="text-4xl">{menu.title || (kids ? "Kids Lemonade Bar" : "Signature Menu")}</div>
        <div className="text-sm text-muted-foreground mt-1">{menu.event_name || e.full_name} · {e.event_date}</div>
        {menu.theme_colors && <div className="text-xs text-olive mt-1">{menu.theme_colors}</div>}
      </div>
      <div className="space-y-6 mt-6">
        {[0, 1, 2].map((i) => {
          const d = drinks[i];
          const name = d.name || fallback[i] || "";
          if (!name && !d.ingredients) return null;
          return (
            <div key={i} className="flex gap-5 items-center pb-6 border-b border-ink/10">
              <img src={DRINK_IMGS[i % DRINK_IMGS.length]} alt={name} style={{ width: 96, height: 96, objectFit: "cover", borderRadius: 16 }} />
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <span style={{ fontFamily: "Cormorant Garamond, serif" }} className="text-2xl">{name || `Drink ${i + 1}`}</span>
                  {!kids && d.mocktail && <span className="text-[10px] uppercase tracking-widest text-olive border border-olive/40 rounded-full px-2 py-0.5">Mocktail available</span>}
                </div>
                <p className="text-sm text-muted-foreground mt-1">{d.ingredients || "Ingredients to be confirmed"}</p>
              </div>
            </div>
          );
        })}
      </div>
      {menu.beverage_note && <p className="text-sm text-muted-foreground mt-4">{menu.beverage_note}</p>}
      {menu.allergy_note && <p className="text-xs text-muted-foreground mt-2">Allergy note: {menu.allergy_note}</p>}
      <p className="text-center text-xs text-olive mt-6">{BRAND.instagram}</p>
    </DocShell>
  );
}

function AlcoholDoc({ e }) {
  const cats = (e.alcohol_list && e.alcohol_list.categories) || generateAlcoholList(e);
  return (
    <DocShell docTitle="Alcohol Shopping List" meta={[{ label: "Event", value: e.event_date }, { label: "Guests", value: e.guest_count }]}>
      <div className="rounded-xl bg-clay p-4 text-xs text-muted-foreground mb-6">{ALCOHOL_DISCLAIMER}</div>
      <InfoGrid rows={[
        { label: "Client", value: e.full_name },
        { label: "Event Date", value: e.event_date },
        { label: "Adults", value: e.adult_count },
        { label: "Service Hours", value: e.service_hours },
      ]} />
      <div className="grid sm:grid-cols-2 gap-x-8 gap-y-6 mt-6">
        {cats.map((cat) => (
          <div key={cat.name}>
            <div style={{ fontFamily: "Cormorant Garamond, serif" }} className="text-xl border-b border-ink/15 pb-1 mb-2">{cat.name}</div>
            {cat.items.map((it, i) => (
              <div key={i} className="flex justify-between text-sm py-0.5"><span className="text-muted-foreground">{it.item}</span><span className="font-medium">{it.qty}</span></div>
            ))}
          </div>
        ))}
      </div>
    </DocShell>
  );
}

function SuppliesDoc({ e }) {
  const items = (e.supplies && e.supplies.items) || [];
  const list = items.length ? items : [];
  return (
    <DocShell docTitle="Supplies Checklist" meta={[{ label: "Event", value: e.event_date }, { label: "Client", value: e.full_name }]}>
      <div className="grid sm:grid-cols-2 gap-x-8 gap-y-1 mt-4">
        {list.map((it, i) => (
          <div key={i} className="flex items-center gap-3 text-sm py-1 border-b border-ink/5">
            <span style={{ width: 16, height: 16, border: "1.5px solid #1A1A1A", borderRadius: 4, display: "inline-block", background: it.checked ? "#1A1A1A" : "transparent" }} />
            <span className={it.checked ? "line-through text-muted-foreground" : ""}>{it.name}</span>
          </div>
        ))}
      </div>
    </DocShell>
  );
}

const TITLES = {
  quote: "Quote", contract: "Contract", invoice: "Invoice", menu: "Menu",
  kids_menu: "Kids Menu", alcohol_list: "Alcohol List", supplies: "Supplies Checklist",
};

export function DocumentModal({ open, onClose, type, event, pricing, squareLink, onPayDeposit }) {
  const ref = useRef(null);
  if (!open) return null;

  const copy = async () => {
    try { await navigator.clipboard.writeText(ref.current?.innerText || ""); toast.success("Copied to clipboard"); }
    catch { toast.error("Copy failed"); }
  };

  const payDeposit = () => {
    if (squareLink) window.open(squareLink, "_blank");
    onPayDeposit && onPayDeposit();
  };

  let body = null;
  if (type === "quote") body = <QuoteDoc e={event} pricing={pricing} />;
  else if (type === "contract") body = <ContractDoc e={event} pricing={pricing} />;
  else if (type === "invoice") body = <InvoiceDoc e={event} pricing={pricing} />;
  else if (type === "menu") body = <MenuDoc e={event} />;
  else if (type === "kids_menu") body = <MenuDoc e={event} kids />;
  else if (type === "alcohol_list") body = <AlcoholDoc e={event} />;
  else if (type === "supplies") body = <SuppliesDoc e={event} />;

  return (
    <div className="fixed inset-0 z-[60] bg-ink/60 overflow-auto" data-testid="document-modal">
      <div className="no-print sticky top-0 z-10 flex items-center justify-between bg-white border-b border-line px-4 py-3">
        <span className="font-serif text-lg">{TITLES[type]} Preview</span>
        <div className="flex gap-2">
          {(type === "quote" || type === "invoice") && (
            <button onClick={payDeposit} disabled={!squareLink} title={squareLink ? "Opens the Square payment link" : "Add a Square link in the Documents tab first"} className="btn-dark text-xs py-2 px-4 disabled:opacity-40" data-testid="doc-pay-deposit"><CreditCard className="h-3 w-3" /> Pay Deposit</button>
          )}
          <button onClick={copy} className="btn-outline text-xs py-2 px-4" data-testid="doc-copy"><Copy className="h-3 w-3" /> Copy</button>
          <button onClick={() => window.print()} className="btn-dark text-xs py-2 px-4" data-testid="doc-print"><Printer className="h-3 w-3" /> Print / Save PDF</button>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-clay" data-testid="doc-close"><X className="h-4 w-4" /></button>
        </div>
      </div>
      <div className="p-4 sm:p-8" ref={ref}>
        <div className="shadow-xl rounded-lg overflow-hidden">{body}</div>
      </div>
    </div>
  );
}

export default DocumentModal;
